import {
    Box,
    Flex,
    Input,
    Popover,
    PopoverAnchor,
    PopoverBody,
    PopoverContent,
    Portal,
    Spinner,
    Stack,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { Portfolio, PortfolioBase } from '../_openapi/portfolio-optimizer';
import { usePortfolioLookup } from '../api/portfolio';
import { readLocalStorage } from '../utils/localStorage';
import { DebouncedInput } from './DebouncedInput';

export const PortfolioSelector = ({
    onSelect,
    identifier = '',
    isInvalid = false,
    isDisabled = false,
    resetOnSelect,
    placeholder,
}: {
    onSelect: (portfolio: Portfolio) => void;
    identifier?: string;
    isInvalid?: boolean;
    isDisabled?: boolean;
    resetOnSelect?: boolean;
    placeholder?: string;
}) => {
    const [search, setSearch] = useState(identifier);
    const { data, isLoading, isPending } = usePortfolioLookup(search);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        setSearch(identifier);
    }, [identifier]);

    const initialized = useRef(false);
    useEffect(() => {
        if (initialized.current) {
            return;
        }

        if (!isLoading && !isOpen) {
            onSelect(data?.length === 1 ? data[0] : ({} as Portfolio));
            initialized.current = true;
        }
    }, [data, isLoading, isOpen, onSelect]);

    const inputRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (ev: any) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(ev.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(ev.target)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const isDevMode = readLocalStorage('DEV_MODE', false);
    if (isDevMode) {
        return (
            <Input
                placeholder="DEV MODE | use p_id"
                size="sm"
                value={search}
                onChange={(ev) => {
                    const value = ev.target.value;
                    onSelect({
                        base: { pId: value, portfolioGuid: value } as PortfolioBase,
                    });
                }}
            />
        );
    }

    return (
        <Popover autoFocus={false} isOpen={isOpen} onClose={onClose} isLazy>
            <PopoverAnchor>
                <Box ref={inputRef} width="100%">
                    <DebouncedInput
                        isInvalid={isInvalid}
                        initialValue={search}
                        debounceCallback={setSearch}
                        autoComplete="off"
                        onFocus={onOpen}
                        size="sm"
                        disabled={isDisabled}
                        placeholder={placeholder}
                    />
                </Box>
            </PopoverAnchor>
            <Portal>
                <PopoverContent width="fit-content" fontSize="sm" boxShadow="dark-lg">
                    <PopoverBody>
                        <Stack gap={0} ref={dropdownRef}>
                            {search.length ? (
                                isPending ? (
                                    <Spinner size="sm" />
                                ) : data?.length ? (
                                    <Box>
                                        <Flex px={2} py={1} fontSize="xs" borderBottomWidth={1}>
                                            <Text as="span" width="300px">
                                                Portfolio GUID
                                            </Text>
                                            <Text as="span" width="100px">
                                                Account ID
                                            </Text>
                                            <Text as="span" width="120px">
                                                Account Alias
                                            </Text>
                                            <Text as="span" width="100px">
                                                P_ID
                                            </Text>
                                        </Flex>
                                        <Box maxHeight="340px" overflowY="auto">
                                            {data.map((portfolio) => (
                                                <Flex
                                                    key={portfolio.base.pId}
                                                    px={2}
                                                    py={1}
                                                    _hover={{ background: 'gray.600' }}
                                                    cursor="pointer"
                                                    borderRadius="sm"
                                                    onClick={() => {
                                                        onSelect(portfolio);
                                                        resetOnSelect && setSearch('');
                                                        onClose();
                                                    }}
                                                    fontSize="xs"
                                                >
                                                    <Text as="span" width="300px">
                                                        {portfolio.base.portfolioGuid}
                                                    </Text>
                                                    <Text as="span" width="100px">
                                                        {portfolio.base.aC}
                                                    </Text>
                                                    <Text as="span" width="120px">
                                                        {portfolio.base.accountAlias}
                                                    </Text>
                                                    <Text as="span" width="100px">
                                                        {portfolio.base.pId}
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Text>
                                        <i>No match found</i>
                                    </Text>
                                )
                            ) : (
                                <Text>
                                    <i>Start typing to search by ID, GUID, Account Alias or Account Number</i>
                                </Text>
                            )}
                        </Stack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};
